
export type Primitive = string | number | boolean | null;
type DisallowedTenantKeys =
  | "practiceId"
  | "activePracticeId"
  | "tenantId"
  | "practice_id"
  | "tenant_id";

type KeyParams = Readonly<Record<string, Primitive>>;

type ForbidTenantKeys<T extends KeyParams> = T & {
  [K in DisallowedTenantKeys]?: never;
};

// Dev-only runtime guard to prevent tenant fields sneaking into query keys
const __FORBIDDEN_KEYS: ReadonlyArray<DisallowedTenantKeys> = [
  "practiceId",
  "activePracticeId",
  "tenantId",
  "practice_id",
  "tenant_id",
];
function assertNoTenantKeys(obj?: KeyParams) {
  if (!import.meta.env.DEV || !obj) return;
  for (const k of Object.keys(obj)) {
    if ((__FORBIDDEN_KEYS as readonly string[]).includes(k)) {
      throw new Error(
        `[qk] Forbidden tenant key in queryKey params: "${k}". Tenant context must come from the provider, not the key.`
      );
    }
  }
}

/**
 * Centralized factory for creating consistent query keys for TanStack Query.
 * Using a factory ensures that keys are created and invalidated uniformly
 * across the application, preventing typos and making refactoring easier.
 *
 * NOTE: Tenant isolation is enforced by Supabase RLS using the practice_id
 * claim in the JWT. We intentionally do NOT include practiceId in keys.
 * On practice switch, we clear the global QueryClient cache to avoid
 * cross-practice stale data.
 *
 * @see {@link https://tkdodo.eu/blog/effective-react-query-keys}
 */
export const qk = {
  // Core, type-safe helpers
  list: <E extends string, P extends KeyParams>(
    entity: E,
    params?: ForbidTenantKeys<P>,
  ) => {
    assertNoTenantKeys(params as KeyParams);
    return ["list", entity, (params ?? {}) as KeyParams] as const;
  },

  detail: <E extends string>(entity: E, id: string | number | "me") =>
    ["detail", entity, id] as const,

  search: <E extends string>(entity: E, term: string) =>
    ["search", entity, term] as const,

  // Global treatment code search with parameters (tenant-agnostic)
  globalSearch: (
    params?: ForbidTenantKeys<{
      codeSystem?: string;
      language?: string;
      searchTerm?: string;
    }>
  ) => {
    assertNoTenantKeys(params as KeyParams);
    return ["globalSearch", params ?? {}] as const;
  },

  team: {
    absences: (practitionerId: string) =>
      ["team", "absences", practitionerId] as const,
    availability: (practitionerId: string) =>
      ["team", "availability", practitionerId] as const,
  },
  practice: {
    closures: () => ["practice", "closures"] as const,
    hours: () => ["practice", "hours"] as const,
  },

  // User and practice related queries
  userProfile: (id: string | "me") =>
    ["detail", "userProfile", id] as const,
  userPractices: (userId: string) => ["userPractices", userId] as const,
  userPracticesWithRoles: (userId: string) =>
    ["userPracticesWithRoles", userId] as const,

  // Global treatment codes and search
  globalCodes: () => ["globalCodes"] as const,
  myCategoryCodes: () => ["myCategoryCodes"] as const,

  // Legacy patient-specific recall queries - migrated to qk.list("recalls", { patient_id: patientId })
  // patientRecalls: (patientId: string) => ["recalls", "patient", patientId] as const,

  // Legacy entity list pattern has been removed - use qk.list(entity, params) instead

  // Inactive patients query
  inactivePatients: () => ["inactivePatients"] as const,

  // Existing recall query
  existingRecall: (patientId: string, procedureId: string) =>
    ["existingRecall", patientId, procedureId] as const,

  // Procedure-related queries
  proceduresByCodes: (codes: string[]) =>
    ["proceduresByCodes", [...codes].sort()] as const,
  implantCodeDetails: (codes?: string[]) =>
    ["implantCodeDetails", codes ?? []] as const,

  // Practice search
  practiceSearch: (query: string) => ["practices", "search", query] as const,
  // Legacy practices query - migrated to qk.list("practices")
  // practices: () => ["practices"] as const,

  // Roles and organisations
  roles: () => ["list", "roles", {}] as const,
  organisations: () => ["list", "organisations", {}] as const,

  // Practice-specific queries
  practiceCodes: (categoryId?: string) =>
    ["practiceCodes", categoryId ?? null] as const,
  practiceCategories: (
    opts?: ForbidTenantKeys<{ codeSystem?: string; language?: string }>
  ) => {
    assertNoTenantKeys(opts as KeyParams);
    return ["practiceCategories", (opts ?? {})] as const;
  },

  // Legacy procedure queries - migrated to qk.list("procedures") and qk.detail("procedures", id)
  // procedures: () => ["procedures"] as const,
  // procedure: (id: string | number) => ["procedure", id] as const,

  // Dental charting specific queries
  oralStatus: (patientId: string) => ["oral-status", patientId] as const,
  appliances: (patientId: string) => ["appliances", patientId] as const,
  patientBaselineConditions: (patientId: string) =>
    ["patientBaselineConditions", patientId] as const,
  toothNotesHistory: (patientId: string, toothCode: string) =>
    ["toothNotesHistory", patientId, toothCode] as const,
  appointmentProcedures: (patientId: string) =>
    ["appointmentProcedures", patientId] as const,
  dentUiMappings: () => ["dent-ui-mappings"] as const,

  // Treatment code suggestions
  extractionSuggestions: (codes: string[]) =>
    ["extractionSuggestions", [...codes].sort()] as const,
  crownSuggestions: (codes: string[]) =>
    ["crownSuggestions", [...codes].sort()] as const,
  endoSuggestions: (codes: string[]) =>
    ["endoSuggestions", [...codes].sort()] as const,
  occlusionCodes: () => ["occlusionCodes"] as const,

  // Scheduling queries (use qk.list('weekly-availability', {start, end}) instead)
  patientAppointmentContext: (patientId: string) =>
    ["patientAppointmentContext", patientId] as const,
  appointmentDetail: (appointmentId: string) =>
    ["appointmentDetail", appointmentId] as const,

  // Patient medications
  patientMedications: (patientId: string) =>
    ["list", "medications", "patient", patientId] as const,

  // Session procedures for patient
  sessionProcedures: (patientId: string) =>
    ["list", "session_procedures", "patient", patientId] as const,

  // Planned procedures for episode
  plannedProcedures: (episodeId: string) =>
    ["list", "planned_procedures", "episode", episodeId] as const,

  // Entity selector query keys
  entitySelectorOptions: (
    entityType: string,
    searchQuery: string,
    filter?: string
  ) => ["entitySelectorOptions", entityType, searchQuery, filter ?? null] as const,

  entitySelectorSelectedOptions: (
    entityType: string,
    value: string | string[] | null
  ) => ["entitySelectorSelectedOptions", entityType, value] as const,

  // Patient name query (used by PatientLink component)
  patientName: (patientId: string) => ["patientName", patientId] as const,

  // Recalls query keys with practice isolation for single-client architecture
  recalls: {
    list: (practiceId: string, view: 'due'|'overdue'|'upcoming'|'all') =>
      ['recalls','list', practiceId, { view }] as const,
    byPatient: (practiceId: string, patientId: string) =>
      ['recalls','byPatient', practiceId, patientId] as const,
    status: (practiceId: string, patientId: string) =>
      ['recalls','status', practiceId, patientId] as const,
    existing: (practiceId: string, patientId: string, procedureId: string) =>
      ['recalls','existing', practiceId, patientId, procedureId] as const,
  },

  // Analytics query keys (legacy - prefer using qk.list() directly)
  analytics: {
    periodontalPdfDaily: () => ["list", "analytics-periodontal-pdf-daily", {}] as const,
    periodontalPdfEvents: () => ["list", "analytics-periodontal-pdf-events", {}] as const,
    reviewWidgetDaily: () => ["list", "analytics-review-widget-daily", {}] as const,
    reviewWidgetEvents: () => ["list", "analytics-review-widget-events", {}] as const,
    // NOTE: appointments and notifications have been migrated to use qk.list() directly
    // Use: qk.list("analytics-appointments", params) instead of qk.analytics.appointments(params)
    // Use: qk.list("analytics-notifications", params) instead of qk.analytics.notifications(params)
  },

} as const;
